import {Helmet} from '@dr.pogodin/react-helmet';
import {Form, FormField, FormSubmitHandler} from 'platform/components';
import {Box} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  useGetDictionaryWorktypeQuery,
  usePatchDictionaryWorktypeMutation,
  usePostDictionaryWorktypeMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

type WorkTypeFormType = {
  name?: string | null;
};

export function WorkTypeDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data, isLoading, isError} = useGetDictionaryWorktypeQuery(
    {workTypeId: id ?? ''},
    {skip: isNil(id)}
  );
  const [postWorkType, {isLoading: isPostLoading}] = usePostDictionaryWorktypeMutation();
  const [patchWorkType, {isLoading: isPatchLoading}] = usePatchDictionaryWorktypeMutation();

  const handleSubmit: FormSubmitHandler<WorkTypeFormType> = async (
    formData,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (isNil(formData?.name)) {
      return;
    }

    const body = {
      name: formData.name,
      isDefault: false,
      isActive: data?.isActive ?? true,
    };

    if (isNil(id)) {
      await postWorkType({
        body,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.workType);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await patchWorkType({
      workTypeId: id as string,
      body,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.workType);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const titleKey = isNil(id) ? 'page.settings.actions.newWorkType' : 'entity.order.labels.workType';

  return (
    <>
      <Helmet title={i18n.t(titleKey)} />
      <SettingsTemplate
        header={{
          title: data?.name ?? i18n.t('page.settings.actions.newWorkType'),
          breadcrumbs: [
            {
              label: i18n.t('entity.order.labels.workType'),
              href: settingsRoutes.workType,
            },
          ],
        }}
        isLoading={isLoading}
        isError={isError}
        isCreating={isNil(id)}
        data-testid={testIds.settings.workTypeDetail('template')}
      >
        <Form<WorkTypeFormType>
          onSubmit={handleSubmit}
          defaultValues={data}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, navigateWithoutBlocker) => (
            <>
              <Box width={85}>
                <FormField
                  control={control}
                  name="name"
                  type="text"
                  label={i18n.t('general.labels.name')}
                  isRequired
                  data-testid={testIds.settings.workTypeDetail('name')}
                />
              </Box>
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.discardChanges'),
                    variant: 'secondary',
                    onClick: () => navigateWithoutBlocker(settingsRoutes.workType),
                  },
                  {
                    control,
                    type: 'form-button',
                    buttonType: 'submit',
                    title: i18n.t('general.actions.saveChanges'),
                    isLoading: isPostLoading || isPatchLoading,
                  },
                ]}
                data-testid={testIds.settings.workTypeDetail('footer')}
              />
            </>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}
